import React, { useState, useRef, useCallback, useEffect } from "react";
import { observer } from "mobx-react";
import { TextField, Select, Button } from "@ann/web-components";
import {
  FiltersContainerWrapper,
  FiltersColumn,
  SearchContainer,
} from "./FiltersContainer.styled";

const FiltersContainer = observer(
  ({
    loading,
    filters,
    filterHandlers,
    filterUI,
  }) => {
    const [localSearchValue, setLocalSearchValue] = useState(filters?.searchValue || "");
    const searchTimeoutRef = useRef(null);
  
    // Desestructurar valores de los objetos agrupados
    const {
      statusOptions,
      modelOptions,
      lastUpdateOptions,
      filteredStatus,
      filteredModel,
      filteredLastUpdate,
      selectedStatus,
      selectedModel,
      selectedLastUpdatePreset,
      searchValue,
      hasActiveFilters,
    } = filters;

    const {
      handleStatusChange,
      handleModelChange,
      handleLastUpdatePresetChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    } = filterHandlers;

    const {
      filtersKey,
      updateLocalSearch,
    } = filterUI;

    const handleSearchChangeCallback = useCallback((value) => {
      setLocalSearchValue(value);
      
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set new timeout for 500ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        if (handleSearchChange) {
          handleSearchChange(value);
        }
      }, 500);
    }, [handleSearchChange]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    // Sync local state with filters when it changes
    useEffect(() => {
      setLocalSearchValue(searchValue || "");
    }, [searchValue]);

    return (
      <FiltersContainerWrapper>
        <FiltersColumn>
          <SearchContainer>
            <TextField
              iconLeft="search"
              onChange={handleSearchChangeCallback}
              size="small"
              placeholder="Search"
              value={localSearchValue}
              disabled={loading}
            />
          </SearchContainer>
          
          <Select
            key={`status-filter-${filtersKey}`}
            label="Status"
            placeholder="Status"
            onChange={handleStatusChange}
            size="small"
            value={
              statusOptions.find((option) => option.value === selectedStatus) || undefined
            }
            width="auto"
            minWidth="101px"
            disabled={loading}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("status", value)}
                label=""
              />
              {filteredStatus.map((status) => (
                <Select.Item
                  key={status.value}
                  value={status.value}
                  label={status.label}
                >
                  {status.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          <Select
            key={`model-filter-${filtersKey}`}
            label="Model"
            placeholder="Model"
            onChange={handleModelChange}
            size="small"
            value={
              modelOptions.find((option) => option.value === selectedModel) || undefined
            }
            width="auto"
            minWidth="103px"
            disabled={loading}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("model", value)}
                label=""
              />
              {filteredModel.map((model) => (
                <Select.Item key={model.value} value={model.value} label={model.label}>
                  {model.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          <Select
            key={`lastUpdate-filter-${filtersKey}`}
            label="Last Update"
            placeholder="Last Update"
            onChange={handleLastUpdatePresetChange}
            size="small"
            value={
              lastUpdateOptions.find((option) => option.value === selectedLastUpdatePreset) || undefined
            }
            width="auto"
            minWidth="141px"
            disabled={loading}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("lastUpdate", value)}
                label=""
              />
              {filteredLastUpdate.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          {hasActiveFilters && (
            <Button
              icon=""
              iconLeft=""
              iconRight=""
              label="Clear Filters"
              size="medium"
              type="secondary"
              onClick={handleClearFiltersWithKeys}
              disabled={loading}
            />
          )}
        </FiltersColumn>
      </FiltersContainerWrapper>
    );
  }
);

export default FiltersContainer;

